import { createAsyncThunk } from '@reduxjs/toolkit';
import { push } from 'redux-first-history';
import cloneDeep from 'lodash/cloneDeep';
import concat from 'lodash/concat';
import includes from 'lodash/includes';
import map from 'lodash/map';
import remove from 'lodash/remove';

import ROUTES from '../../constants/routes';
import { removeTokens } from '../../helpers/auth/tokens';
import WebAPIClient, { errorResponseToastr } from '../../api';
import { showLoading, hideLoading } from '../app';
import { getOrganizations } from '../organizations';
import { getRTOs, getPeakData } from '../rtos';
import { setLoader } from '../pages';
import { user as initialState } from '../initialState';
import { toastr } from '../../components/CustomToast';

const authenticateUser = createAsyncThunk(
  'user/authenticateUser',
  async (_, { dispatch }) => {
    try {
      const { user, memberships } = await new WebAPIClient().GET(
        '/resource/auth'
      );
      const subscriptions = await new WebAPIClient().GET(
        `/resource/subscriptions/${user.user_id}`
      );

      dispatch(getOrganizations(user));
      await dispatch(getRTOs());
      dispatch(getPeakData('PJM'));

      return { item: user, memberships, subscriptions };
    } catch (err) {
      console.error('Authentication Error: ', err);
      dispatch(push(ROUTES.UNAUTH.LOGIN));
      dispatch(setLoader(false));
    }
  }
);

const logoutUser = createAsyncThunk(
  'user/logoutUser',
  async (_, { dispatch }) => {
    try {
      setLoader(true);
      removeTokens();
      return initialState;
    } catch (err) {
      console.error('LOGOUT FAILED', err);
    } finally {
      dispatch(push(ROUTES.UNAUTH.LOGIN));
      setLoader(false);
    }
  }
);

const createSubscriptions = createAsyncThunk(
  'user/createSubscriptions',
  async (_subscriptions, { dispatch, getState }) => {
    const { subscriptions } = getState().user;
    let subscriptionsAdded = [];
    try {
      dispatch(showLoading());
      let allSubscriptions = cloneDeep(subscriptions);

      subscriptionsAdded = await Promise.all(
        map(
          _subscriptions,
          async (sub) =>
            await new WebAPIClient().POST(
              `/resource/subscriptions/${sub.userId}`,
              sub
            )
        )
      );

      toastr.success({
        title: 'Alarm Subscription created',
      });
      return {
        subscriptions: concat(allSubscriptions, subscriptionsAdded),
      };
    } catch (err) {
      errorResponseToastr(err);
    } finally {
      dispatch(hideLoading());
    }
  }
);

const deleteSubscriptions = createAsyncThunk(
  'user/deleteSubscriptions',
  async (_subscriptions, { dispatch, getState }) => {
    const { subscriptions } = getState().user;
    let subscriptionsRemoved = [];
    try {
      dispatch(showLoading());
      let allSubscriptions = cloneDeep(subscriptions);
      subscriptionsRemoved = await Promise.all(
        map(
          _subscriptions,
          async (sub) =>
            await new WebAPIClient().DELETE(
              `/resource/subscriptions/${sub.subscription_id}`
            )
        )
      );

      // update store
      remove(allSubscriptions, (subscription) =>
        includes(subscriptionsRemoved, subscription.subscription_id)
      );

      toastr.success({
        title: 'Alarm Subscription deleted',
      });
      return {
        subscriptions: allSubscriptions,
      };
    } catch (err) {
      errorResponseToastr(err);
    } finally {
      dispatch(hideLoading());
    }
  }
);

export {
  authenticateUser,
  logoutUser,
  createSubscriptions,
  deleteSubscriptions,
};
